import type { SupportedLangCode } from '@/application/i18n/language'
import type { TtsVoice } from '@/domains/types'
import type { PrepareOptions, SpeakCallbacks, SpeakRequest, TtsEngine } from './tts-engine'
import {
  isPiperResponse,
  type PiperCommand,
  type PiperRequest,
  type PiperResponse,
} from './piper-protocol'
import { getVoiceEntry, type PiperVoiceEntry } from './voice-catalogue'
import { SILENT_WAV_DATA_URI } from './wav'
import { createPiperWorker } from './piper-worker-factory'

/** The slice of `Worker` the engine relies on — lets tests inject a fake. */
export interface PiperWorkerLike {
  postMessage(message: PiperRequest): void
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
  addEventListener(type: 'error' | 'messageerror', listener: () => void): void
  terminate(): void
}

export interface PiperEngineDeps {
  createWorker: () => PiperWorkerLike
  createAudio: () => HTMLAudioElement
  /** Whether Web Workers, WebAssembly and audio playback all exist. */
  isEnvironmentSupported: () => boolean
  getVoice: (lang: SupportedLangCode) => PiperVoiceEntry
}

interface Clip {
  blob: Blob
  durationSeconds: number
}

interface SynthJob {
  cacheKey: string
  lang: SupportedLangCode
  text: string
  started: boolean
  promise: Promise<Clip>
  resolve: (clip: Clip) => void
  reject: (error: Error) => void
}

interface VoiceLoad {
  promise: Promise<void>
  listeners: Set<(fraction: number) => void>
}

interface PendingRequest {
  resolve: (response: PiperResponse) => void
  reject: (error: Error) => void
  onProgress?: (loaded: number, total: number) => void
}

const SUPPORTED_LANGS: readonly SupportedLangCode[] = ['fr', 'en', 'es']

function toSupportedLang(lang: string): SupportedLangCode {
  const prefix = lang.replace(/_/g, '-').toLowerCase().split('-')[0]
  const match = SUPPORTED_LANGS.find((supported) => supported === prefix)
  if (!match) throw new Error(`No site voice for language: ${lang}`)
  return match
}

function abortError(): Error {
  return new DOMException('Preparation aborted', 'AbortError')
}

function defaultEnvironmentSupported(): boolean {
  return (
    typeof Worker === 'function' &&
    typeof WebAssembly === 'object' &&
    typeof Audio === 'function' &&
    typeof URL.createObjectURL === 'function'
  )
}

/**
 * `TtsEngine` speaking with the site's own Piper voices, synthesized inside a
 * Web Worker (WASM) and played through ONE shared `<audio>` element (a single
 * element is what keeps iOS' user-gesture unlock valid from segment to
 * segment, and what the Media Session / lock screen attaches to).
 *
 * Synthesis is serial: demand requests jump the queue, prefetches fill the
 * gaps. A voice model is downloaded lazily, per language, and only through
 * `prepare` / a request for that language.
 */
export class PiperEngine implements TtsEngine {
  // eslint-disable-next-line id-length -- required literal property name from the `TtsEngine` interface
  readonly id = 'piper' as const

  private worker: PiperWorkerLike | null = null
  private audio: HTMLAudioElement | null = null
  private nextRequestId = 1
  private readonly pending = new Map<number, PendingRequest>()

  private readonly loadedLangs = new Set<SupportedLangCode>()
  private readonly voiceLoads = new Map<SupportedLangCode, VoiceLoad>()

  private readonly clips = new Map<string, Clip>()
  private readonly jobs = new Map<string, SynthJob>()
  private queue: SynthJob[] = []
  private busy = false

  private generation = 0
  private objectUrl: string | null = null
  private hasActiveClip = false
  private rate = 1

  constructor(private readonly deps: PiperEngineDeps) {}

  isSupported(): boolean {
    return this.deps.isEnvironmentSupported()
  }

  async loadVoices(): Promise<TtsVoice[]> {
    return []
  }

  onVoicesChanged(): () => void {
    return () => {}
  }

  isPrepared(lang: string): boolean {
    try {
      return this.loadedLangs.has(toSupportedLang(lang))
    } catch {
      return false
    }
  }

  // ─── Worker plumbing ───────────────────────────────────────────────────────

  private ensureWorker(): PiperWorkerLike {
    if (this.worker) return this.worker
    const worker = this.deps.createWorker()
    worker.addEventListener('message', (event) => this.handleMessage(event.data))
    const crash = () => this.handleWorkerCrash(worker)
    worker.addEventListener('error', crash)
    worker.addEventListener('messageerror', crash)
    this.worker = worker
    return worker
  }

  private handleMessage(data: unknown): void {
    if (!isPiperResponse(data)) return
    const request = this.pending.get(data.requestId)
    if (!request) return
    if (data.type === 'progress') {
      request.onProgress?.(data.loaded, data.total)
      return
    }
    this.pending.delete(data.requestId)
    if (data.type === 'error') request.reject(new Error(data.message))
    else request.resolve(data)
  }

  private handleWorkerCrash(crashed: PiperWorkerLike): void {
    if (this.worker !== crashed) return
    crashed.terminate()
    this.worker = null
    this.loadedLangs.clear()
    const failed = [...this.pending.values()]
    this.pending.clear()
    for (const request of failed) request.reject(new Error('Piper worker crashed'))
  }

  private send(
    message: PiperCommand,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<PiperResponse> {
    const worker = this.ensureWorker()
    const requestId = this.nextRequestId++
    return new Promise<PiperResponse>((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject, onProgress })
      worker.postMessage({ ...message, requestId })
    })
  }

  // ─── Voice models ──────────────────────────────────────────────────────────

  private ensureVoice(lang: SupportedLangCode, listener?: (fraction: number) => void) {
    if (this.loadedLangs.has(lang)) {
      listener?.(1)
      return Promise.resolve()
    }
    const existing = this.voiceLoads.get(lang)
    if (existing) {
      if (listener) existing.listeners.add(listener)
      return existing.promise
    }

    const listeners = new Set<(fraction: number) => void>()
    if (listener) listeners.add(listener)
    const voice = this.deps.getVoice(lang)
    const promise = this.send({ type: 'load', voice }, (loaded, total) => {
      const fraction = total > 0 ? Math.min(1, loaded / total) : 0
      for (const notify of listeners) notify(fraction)
    })
      .then(() => {
        this.loadedLangs.add(lang)
        this.voiceLoads.delete(lang)
        for (const notify of listeners) notify(1)
        this.pump()
      })
      .catch((error: Error) => {
        this.voiceLoads.delete(lang)
        this.failJobsForLang(lang, error)
        throw error
      })
    this.voiceLoads.set(lang, { promise, listeners })
    return promise
  }

  async prepare(langs: readonly string[], options: PrepareOptions = {}): Promise<void> {
    const { onProgress, signal } = options
    const targets = [...new Set(langs.map(toSupportedLang))]
    if (signal?.aborted) throw abortError()

    const fractions = new Map<SupportedLangCode, number>(targets.map((lang) => [lang, 0]))
    const report = () => {
      const total = [...fractions.values()].reduce((accumulated, value) => accumulated + value, 0)
      onProgress?.(total / Math.max(targets.length, 1))
    }
    report()

    const loading = Promise.all(
      targets.map((lang) =>
        this.ensureVoice(lang, (fraction) => {
          fractions.set(lang, fraction)
          report()
        }),
      ),
    ).then(() => undefined)

    if (!signal) return loading
    await new Promise<void>((resolve, reject) => {
      const onAbort = () => reject(abortError())
      signal.addEventListener('abort', onAbort, { once: true })
      loading.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort))
    })
  }

  // ─── Synthesis queue ───────────────────────────────────────────────────────

  private static keyOf(lang: string, text: string): string {
    return `${lang}\u0000${text}`
  }

  private getClip(request: SpeakRequest, priority: 'demand' | 'prefetch'): Promise<Clip> {
    const lang = toSupportedLang(request.lang)
    const cacheKey = PiperEngine.keyOf(lang, request.text)
    const cached = this.clips.get(cacheKey)
    if (cached) return Promise.resolve(cached)

    const existing = this.jobs.get(cacheKey)
    if (existing) {
      if (priority === 'demand' && !existing.started) {
        this.queue = [existing, ...this.queue.filter((task) => task !== existing)]
      }
      return existing.promise
    }

    let resolve: (clip: Clip) => void = () => {}
    let reject: (error: Error) => void = () => {}
    const promise = new Promise<Clip>((resolveJob, rejectJob) => {
      resolve = resolveJob
      reject = rejectJob
    })
    const task: SynthJob = {
      cacheKey,
      lang,
      text: request.text,
      started: false,
      promise,
      resolve,
      reject,
    }
    this.jobs.set(cacheKey, task)
    if (priority === 'demand') this.queue.unshift(task)
    else this.queue.push(task)
    this.pump()
    return promise
  }

  private failJobsForLang(lang: SupportedLangCode, error: Error): void {
    const failed = this.queue.filter((task) => task.lang === lang)
    this.queue = this.queue.filter((task) => task.lang !== lang)
    for (const task of failed) {
      this.jobs.delete(task.cacheKey)
      task.reject(error)
    }
  }

  private pump(): void {
    if (this.busy) return
    const task = this.queue.find((candidate) => this.loadedLangs.has(candidate.lang))

    // Jobs waiting on a model that is not loaded yet trigger its download
    // without blocking the ones that can already run.
    for (const waiting of this.queue) {
      if (!this.loadedLangs.has(waiting.lang)) this.ensureVoice(waiting.lang).catch(() => {})
    }
    if (!task) return

    this.queue = this.queue.filter((candidate) => candidate !== task)
    task.started = true
    this.busy = true
    const voiceId = this.deps.getVoice(task.lang).id

    this.send({ type: 'synthesize', voiceId, text: task.text })
      .then((response) => {
        if (response.type !== 'audio') throw new Error('Unexpected worker response')
        const clip: Clip = {
          blob: new Blob([response.wav], { type: 'audio/wav' }),
          durationSeconds: response.durationSeconds,
        }
        this.clips.set(task.cacheKey, clip)
        task.resolve(clip)
      })
      .catch((error: Error) => task.reject(error))
      .finally(() => {
        this.jobs.delete(task.cacheKey)
        this.busy = false
        this.pump()
      })
  }

  prefetch(request: SpeakRequest): void {
    try {
      this.getClip(request, 'prefetch').catch(() => {})
    } catch {
      // Unsupported language: nothing to prefetch.
    }
  }

  releaseCache(): void {
    this.clips.clear()
    const dropped = this.queue
    this.queue = []
    for (const task of dropped) {
      this.jobs.delete(task.cacheKey)
      task.promise.catch(() => {})
      task.reject(new Error('Released'))
    }
  }

  // ─── Playback ──────────────────────────────────────────────────────────────

  private ensureAudio(): HTMLAudioElement {
    if (!this.audio) {
      this.audio = this.deps.createAudio()
      this.audio.preservesPitch = true
    }
    return this.audio
  }

  private applyRate(audio: HTMLAudioElement): void {
    audio.defaultPlaybackRate = this.rate
    audio.playbackRate = this.rate
    audio.preservesPitch = true
  }

  unlock(): void {
    if (this.hasActiveClip) return
    const audio = this.ensureAudio()
    audio.src = SILENT_WAV_DATA_URI
    audio.play().catch(() => {})
  }

  speak(request: SpeakRequest, callbacks: SpeakCallbacks): void {
    this.cancel()
    const generation = this.generation
    this.rate = request.rate

    const play = (clip: Clip) => {
      if (generation !== this.generation) return
      this.play(clip, generation, callbacks)
    }

    let cached: Clip | undefined
    try {
      cached = this.clips.get(PiperEngine.keyOf(toSupportedLang(request.lang), request.text))
    } catch {
      callbacks.onError('failed')
      return
    }
    // Cached clips start synchronously, so a segment change keeps running
    // inside the previous segment's `ended` event (needed for iOS background
    // playback).
    if (cached) {
      play(cached)
      return
    }

    this.getClip(request, 'demand').then(play, () => {
      if (generation === this.generation) callbacks.onError('failed')
    })
  }

  private play(clip: Clip, generation: number, callbacks: SpeakCallbacks): void {
    const audio = this.ensureAudio()
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = URL.createObjectURL(clip.blob)

    let started = false
    // `cancel()` (which every `speak()` starts with) detaches these handlers,
    // so a handler that fires always belongs to the current clip.
    audio.onplaying = () => {
      if (started) return
      started = true
      callbacks.onStart()
    }
    audio.onended = () => {
      this.hasActiveClip = false
      callbacks.onEnd()
    }
    audio.onerror = () => {
      this.hasActiveClip = false
      callbacks.onError('failed')
    }

    audio.src = this.objectUrl
    this.applyRate(audio)
    this.hasActiveClip = true
    callbacks.onDuration?.(clip.durationSeconds)
    audio.play().catch(() => {
      if (generation !== this.generation) return
      this.hasActiveClip = false
      callbacks.onError('failed')
    })
  }

  pause(): void {
    this.audio?.pause()
  }

  resume(): boolean {
    if (!this.audio || !this.hasActiveClip) return false
    this.audio.play().catch(() => {})
    return true
  }

  setLiveRate(rate: number): void {
    this.rate = rate
    if (this.audio) this.applyRate(this.audio)
  }

  getProgress(): number | null {
    const audio = this.audio
    if (!audio || !this.hasActiveClip) return null
    const { duration, currentTime } = audio
    if (!Number.isFinite(duration) || duration <= 0) return null
    return Math.min(1, currentTime / duration)
  }

  cancel(): void {
    this.generation += 1
    this.hasActiveClip = false
    if (this.audio) {
      this.audio.onplaying = null
      this.audio.onended = null
      this.audio.onerror = null
      this.audio.pause()
    }
  }

  isSpeaking(): boolean {
    return this.audio !== null && !this.audio.paused && !this.audio.ended
  }
}

let singleton: PiperEngine | null = null

/** Process-wide `PiperEngine` — one worker, one audio element, one clip cache. */
export function getPiperEngine(): PiperEngine {
  singleton ??= new PiperEngine({
    createWorker: createPiperWorker,
    createAudio: () => new Audio(),
    isEnvironmentSupported: defaultEnvironmentSupported,
    getVoice: getVoiceEntry,
  })
  return singleton
}
